/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Col, Collapse, Form, InputNumber, Row, Typography } from 'antd';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';
import { ArrowDownIcon } from '../../../Icons/ArrowDownIcon';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { NODE_TYPES } from '../../../../constants/flowGraph';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import { URL_REGEX } from '../../../../constants/regex';

// Constant declarations
const { Paragraph, Text } = Typography;

const OptionNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const [showConflictMessage, setShowConflictMessage] = useState(false);

  const isValidUrl = (text) => {
    if (text && !URL_REGEX.test(text) && !/mailto:([^?]*)/.test(text)) {
      return false;
    }
    return true;
  };

  return (
    <Fragment>
      <LabelWithOriginalText label={t('Option text')} originalText={form.getFieldValue(['data', 'label', primaryBotLanguage])} />

      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'label', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Option text cannot be blank.') }]}
          >
            <RichTextEditor
              toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
              placeholder={t('Enter the option text')}
              hasFloatingToolbar={true}
              handleAsPlainText={true}
              elementStyles={{ p: { marginBottom: 0 } }}
            />
          </Form.Item>
        ))
      }

      {
        selectedElement.type === NODE_TYPES.LINK_OPTION ? (
          <Form.Item
            label={t('URL')}
            name={['data', 'linkUrl']}
            rules={[
              {
                validator: (_rule, value) => {
                  // Skip validation if no value provided
                  if (!value) {
                    setShowConflictMessage(false);
                    return Promise.resolve();
                  }

                  const isValidURL = isValidUrl(value);
                  setShowConflictMessage(!isValidURL);

                  return isValidURL ? Promise.resolve() : Promise.reject();
                },
                warningOnly: true,
                message: t('This may not be a valid URL')
              }
            ]}
            validateStatus={showConflictMessage ? 'warning' : undefined}
          >
            <RichTextEditor
              toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE]}
              placeholder={t('Enter a URL')}
              hasFloatingToolbar={true}
              handleAsPlainText={true}
              elementStyles={{ p: { marginBottom: 0 } }}
            />
          </Form.Item>
        ) : null
      }

      <Collapse
        bordered={false}
        expandIcon={({ isActive }) => <ArrowDownIcon rotate={isActive ? 180 : 0} />}
      >
        <Collapse.Panel
          header={t('Advanced Settings')}
          forceRender
        >
          <Row>
            <Col span={24}>
              <Text>
                {t('Tooltip')}
              </Text>
            </Col>
            <Col span={24}>
              <Paragraph type="secondary">
                {t('This text will appear when the user hovers over the option button. Available only for webchat agents.')}
              </Paragraph>
            </Col>
          </Row>

          <Form.Item
            name={['data', 'tooltip']}
          >
            <RichTextEditor
              toolbarActions={[RICH_TEXT_TOOLBAR_ACTIONS.BOLD, RICH_TEXT_TOOLBAR_ACTIONS.ITALIC, RICH_TEXT_TOOLBAR_ACTIONS.EMOJI, RICH_TEXT_TOOLBAR_ACTIONS.LINK]}
              placeholder={t('Enter a tooltip text')}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>

          <Form.Item
            label={t('Priority')}
            name={['data', 'priority']}
          >
            <InputNumber
              size="large"
              min={1}
              defaultValue={1}
              max={99}
              required
              parser={value => !value ? 1 : value}
              disabled={!isEditingPrimaryLanguage}
            />
          </Form.Item>
        </Collapse.Panel>
      </Collapse>
    </Fragment>
  );
};

OptionNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default OptionNodeForm;
